import { authFetch } from './http';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/efx-csp/api/v1';

export type IssueType = 'MISSING_IN_PLATFORM' | 'MISSING_IN_CRM' | 'MISSING_IN_MPESA' | string;
export type TransactionType = 'DEPOSIT' | 'WITHDRAWAL';
export type Source = 'MPESA' | 'MTT' | 'MT5' | 'CRM';

export interface ReconciliationIssue {
  issueType: IssueType;
  transactionType: TransactionType;
  crmId?: string;
  mtTicket?: string;
  fullName?: string;
  processedCurrency?: string;
  finalMpesaRef?: string;
  amount?: number;
  phone?: string;
  account?: string;
  narration?: string;
  processedAt?: string;
  detectedAt?: string;
  ageMinutes?: number;
}

export interface ReconciliationResult {
  id: number;
  reportingDate: string;
  depositRate: number;
  withdrawalRate: number;
  mt5DepositCount: number;
  mt5DepositTotal: number;
  mttDepositCount: number;
  mttDepositTotal: number;
  mpesaDepositCount: number;
  mpesaDepositTotal: number;
  crmDepositCount: number;
  crmDepositTotal: number;
  matchedDepositCount: number;
  unmatchedDepositCount: number;
  depositProfitLoss: number;
  mt5WithdrawalCount: number;
  mt5WithdrawalTotal: number;
  mttWithdrawalCount: number;
  mttWithdrawalTotal: number;
  mpesaWithdrawalCount: number;
  mpesaWithdrawalTotal: number;
  crmWithdrawalCount: number;
  crmWithdrawalTotal: number;
  matchedWithdrawalCount: number;
  unmatchedWithdrawalCount: number;
  withdrawalProfitLoss: number;
  issues: ReconciliationIssue[];
}

export interface ClientTransactions {
  deposits: ReconciliationIssue[];
  withdrawals: ReconciliationIssue[];
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  dateTime?: string;
  data?: T;
  /** HTTP status of the underlying response, populated by this client (not sent by the server) */
  status?: number;
}

export interface DateRangeFilters {
  dateFrom?: string;
  dateTo?: string;
}

export interface HistoryFilters {
  date?: string;
  type?: TransactionType;
}

async function parseResponse<T>(res: Response): Promise<ApiResponse<T>> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      success: false,
      status: res.status,
      message: data?.message ?? `Request failed: ${res.status} ${res.statusText}`,
    };
  }
  return { ...(data as ApiResponse<T>), status: res.status };
}

function toErrorResponse<T>(error: unknown): ApiResponse<T> {
  const message = error instanceof Error ? error.message : 'A network error occurred.';
  return { success: false, message };
}

function buildQuery(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value?.trim()) query.set(key, value.trim());
  });
  return query.toString();
}

export async function uploadReconciliation(
  files: {
    mt5File: File;
    mttFile: File;
    mpesaDepositsFile: File;
    mpesaWithdrawalsFile: File;
  },
  rates: { depositRate: number; withdrawalRate: number },
  reportingDate: string,
): Promise<ApiResponse<ReconciliationResult>> {
  const query = buildQuery({
    depositRate: String(rates.depositRate),
    withdrawalRate: String(rates.withdrawalRate),
    reportingDate,
  });
  const url = `${apiBaseUrl}/reconciliation/upload?${query}`;

  const formData = new FormData();
  formData.append('mt5File', files.mt5File);
  formData.append('mttFile', files.mttFile);
  formData.append('mpesaDepositsFile', files.mpesaDepositsFile);
  formData.append('mpesaWithdrawalsFile', files.mpesaWithdrawalsFile);

  try {
    const res = await authFetch(url, {
      method: 'POST',
      headers: { Accept: '*/*' },
      body: formData,
    });
    return parseResponse<ReconciliationResult>(res);
  } catch (error) {
    return toErrorResponse<ReconciliationResult>(error);
  }
}

async function getTransactionsBySource(
  endpoint: 'deposits' | 'withdrawals',
  source: Source,
  reportingDate: string,
  filters: DateRangeFilters = {},
): Promise<ApiResponse<ReconciliationIssue[]>> {
  const query = buildQuery({
    source,
    reportingDate,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });
  const url = `${apiBaseUrl}/reconciliation/transactions/${endpoint}?${query}`;

  try {
    const res = await authFetch(url, { method: 'GET', headers: { Accept: '*/*' } });
    return parseResponse<ReconciliationIssue[]>(res);
  } catch (error) {
    return toErrorResponse<ReconciliationIssue[]>(error);
  }
}

export async function getWithdrawalsBySource(
  source: Source,
  reportingDate: string,
  filters?: DateRangeFilters,
): Promise<ApiResponse<ReconciliationIssue[]>> {
  return getTransactionsBySource('withdrawals', source, reportingDate, filters);
}

export async function getDepositsBySource(
  source: Source,
  reportingDate: string,
  filters?: DateRangeFilters,
): Promise<ApiResponse<ReconciliationIssue[]>> {
  return getTransactionsBySource('deposits', source, reportingDate, filters);
}

export async function getClientTransactions(
  crmId: string,
  reportingDate?: string,
  filters: DateRangeFilters = {},
): Promise<ApiResponse<ClientTransactions>> {
  const query = buildQuery({
    reportingDate,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });
  const url = `${apiBaseUrl}/reconciliation/transactions/clients/${encodeURIComponent(crmId)}${query ? `?${query}` : ''}`;

  try {
    const res = await authFetch(url, { method: 'GET', headers: { Accept: '*/*' } });
    return parseResponse<ClientTransactions>(res);
  } catch (error) {
    return toErrorResponse<ClientTransactions>(error);
  }
}

export async function getReconciliationHistory(
  filters: HistoryFilters = {},
): Promise<ApiResponse<ReconciliationResult[]>> {
  const query = buildQuery({ date: filters.date, type: filters.type });
  const url = `${apiBaseUrl}/reconciliation/history${query ? `?${query}` : ''}`;

  try {
    const res = await authFetch(url, { method: 'GET', headers: { Accept: '*/*' } });
    return parseResponse<ReconciliationResult[]>(res);
  } catch (error) {
    return toErrorResponse<ReconciliationResult[]>(error);
  }
}

export async function evictReconciliationCache(
  reportingDate: string,
): Promise<ApiResponse<void>> {
  const url = `${apiBaseUrl}/reconciliation/cache/${encodeURIComponent(reportingDate)}`;

  try {
    const res = await authFetch(url, { method: 'DELETE', headers: { Accept: '*/*' } });
    if (res.status === 204) return { success: true, status: 204 };
    const parsed = await parseResponse<void>(res);
    if (res.ok && parsed.success === undefined) return { ...parsed, success: true };
    return parsed;
  } catch (error) {
    return toErrorResponse<void>(error);
  }
}

export async function evictReconciliationIssue(
  reportingDate: string,
  crmId: string,
): Promise<ApiResponse<ReconciliationResult>> {
  const url = `${apiBaseUrl}/reconciliation/cache/${encodeURIComponent(reportingDate)}/issues/${encodeURIComponent(crmId)}`;

  try {
    const res = await authFetch(url, { method: 'DELETE', headers: { Accept: '*/*' } });
    return parseResponse<ReconciliationResult>(res);
  } catch (error) {
    return toErrorResponse<ReconciliationResult>(error);
  }
}
